// src/services/aiExtraction.ts
// Calls Supabase Edge Functions — GEMINI_API_KEY is NEVER in browser code.
// These functions only return structured data for human review.
// No automatic DB writes occur here.

import { supabase } from '@/lib/supabase';
import { z } from 'zod';

// ── Zod schemas for frontend validation of Edge Function responses ─────────────

export const extractedInvoiceItemSchema = z.object({
  description: z.string().min(1),
  quantity: z.number().int().positive(),
  unit_cost: z.number().nonnegative(),
});

export const extractedInvoiceSchema = z.object({
  supplier_name: z.string().nullable(),
  invoice_reference: z.string().nullable(),
  invoice_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  items: z.array(extractedInvoiceItemSchema).min(0),
  shipping_cost: z.number().nonnegative(),
  other_cost: z.number().nonnegative(),
  discount: z.number().nonnegative().optional().default(0),
  notes: z.string().nullable(),
});

export const extractedLaptopSpecsSchema = z.object({
  brand: z.string().nullable(),
  model: z.string().nullable(),
  serial_number: z.string().nullable(),
  service_tag: z.string().nullable(),
  cpu: z.string().nullable(),
  ram: z.string().nullable(),
  storage: z.string().nullable(),
  gpu: z.string().nullable(),
  screen_size: z.string().nullable(),
  colour: z.string().nullable(),
  operating_system: z.string().nullable(),
});

export type ExtractedInvoice = z.infer<typeof extractedInvoiceSchema>;
export type ExtractedInvoiceItem = z.infer<typeof extractedInvoiceItemSchema>;
export type ExtractedLaptopSpecs = z.infer<typeof extractedLaptopSpecsSchema>;

// ── AI availability check ─────────────────────────────────────────────────────

export type AiStatus = 'available' | 'not_configured' | 'unknown';

/**
 * Quick probe to see if GEMINI_API_KEY is set on the server.
 * Fails silently — callers should show a disabled state if 'not_configured'.
 */
export async function checkAiAvailability(): Promise<AiStatus> {
  try {
    const { error } = await supabase.functions.invoke('extract-invoice', {
      method: 'POST',
      body: new FormData(), // empty — no file
    });

    if (error) {
      const body = error.context?.body ?? '';
      try {
        const parsed = JSON.parse(typeof body === 'string' ? body : JSON.stringify(body));
        if (parsed?.error === 'not_configured') return 'not_configured';
      } catch {
        // ignore
      }
      return 'available';
    }

    return 'available';
  } catch {
    return 'unknown';
  }
}

// ── Invoice extraction ────────────────────────────────────────────────────────

export interface InvoiceExtractionResult {
  data: ExtractedInvoice | null;
  error: string | null;
  notConfigured: boolean;
}

export async function extractInvoiceData(file: File): Promise<InvoiceExtractionResult> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const { data: responseData, error } = await supabase.functions.invoke('extract-invoice', {
      method: 'POST',
      body: formData,
    });

    if (error) {
      const body = error.context?.body ?? '';
      try {
        const parsed = JSON.parse(typeof body === 'string' ? body : JSON.stringify(body));
        if (parsed?.error === 'not_configured') {
          return { data: null, error: null, notConfigured: true };
        }
        return { data: null, error: parsed?.error ?? 'Extraction failed', notConfigured: false };
      } catch {
        return { data: null, error: 'Extraction failed. Please try again.', notConfigured: false };
      }
    }

    if (!responseData?.success || !responseData?.data) {
      return { data: null, error: 'No data returned from extraction.', notConfigured: false };
    }

    // Zod validate on client side
    const parsed = extractedInvoiceSchema.safeParse(responseData.data);
    if (!parsed.success) {
      return {
        data: null,
        error: 'Extracted data failed validation. Try a clearer image.',
        notConfigured: false,
      };
    }

    return { data: parsed.data, error: null, notConfigured: false };
  } catch {
    return {
      data: null,
      error: 'Could not connect to the extraction service.',
      notConfigured: false,
    };
  }
}

// ── Laptop spec extraction ────────────────────────────────────────────────────

export interface LaptopSpecExtractionResult {
  data: ExtractedLaptopSpecs | null;
  error: string | null;
  notConfigured: boolean;
}

export async function extractLaptopSpecs(file: File): Promise<LaptopSpecExtractionResult> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const { data: responseData, error } = await supabase.functions.invoke('extract-laptop-specs', {
      method: 'POST',
      body: formData,
    });

    if (error) {
      const body = error.context?.body ?? '';
      try {
        const parsed = JSON.parse(typeof body === 'string' ? body : JSON.stringify(body));
        if (parsed?.error === 'not_configured') {
          return { data: null, error: null, notConfigured: true };
        }
        return { data: null, error: parsed?.error ?? 'Extraction failed', notConfigured: false };
      } catch {
        return { data: null, error: 'Extraction failed. Please try again.', notConfigured: false };
      }
    }

    if (!responseData?.success || !responseData?.data) {
      return { data: null, error: 'No data returned from extraction.', notConfigured: false };
    }

    const parsed = extractedLaptopSpecsSchema.safeParse(responseData.data);
    if (!parsed.success) {
      return {
        data: null,
        error: 'Extracted specs failed validation. Try a clearer photo.',
        notConfigured: false,
      };
    }

    return { data: parsed.data, error: null, notConfigured: false };
  } catch {
    return {
      data: null,
      error: 'Could not connect to the extraction service.',
      notConfigured: false,
    };
  }
}

// ── Normalization & Helper Utilities ──────────────────────────────────────────

/**
 * Normalizes supplier name for safe comparison:
 * - trim
 * - lowercase
 * - collapse whitespace
 * - remove basic punctuation
 */
export function normalizeSupplierName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[.,\-_&'"/\\()[\]{}]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Finds an exact normalized match in the supplier list.
 * Returns null if 0 or >1 matches found.
 * Never performs fuzzy auto-selection between different companies.
 */
export function findExactSupplierMatch(
  detectedName: string | null | undefined,
  suppliers: Array<{ id: string; name: string }>
): { id: string; name: string } | null {
  if (!detectedName || !detectedName.trim()) return null;
  const normalizedDetected = normalizeSupplierName(detectedName);
  if (!normalizedDetected) return null;

  const matches = suppliers.filter(
    (s) => normalizeSupplierName(s.name) === normalizedDetected
  );
  return matches.length === 1 ? matches[0] : null;
}

/**
 * Safe presentation normalization for specs:
 * - trim whitespace
 * - "16 gb", "16 GB" -> "16GB"
 * - "512 gb", "512 GB" -> "512GB"
 */
export function normalizeSpecValue(val: string | null | undefined): string | null {
  if (!val) return null;
  const trimmed = val.trim();
  if (!trimmed) return null;
  return trimmed
    .replace(/\b(\d+)\s*(?:gb|g|GB)\b/i, '$1GB')
    .replace(/\b(\d+)\s*(?:tb|t|TB)\b/i, '$1TB');
}

/**
 * Asynchronously checks if a serial number already exists in Supabase laptops.
 */
export async function checkDuplicateSerial(
  serial: string,
  excludeLaptopId?: string
): Promise<{ isDuplicate: boolean; existingLaptop?: { id: string; stock_id: string; brand: string; model: string } }> {
  if (!serial || !serial.trim()) return { isDuplicate: false };
  const clean = serial.trim();
  let query = supabase
    .from('laptops')
    .select('id, stock_id, brand, model')
    .eq('serial_number', clean)
    .limit(1);

  if (excludeLaptopId) {
    query = query.neq('id', excludeLaptopId);
  }

  const { data, error } = await query;
  if (!error && data && data.length > 0) {
    return { isDuplicate: true, existingLaptop: data[0] };
  }
  return { isDuplicate: false };
}

// ── Conservative Item Classification for Purchase Rows ─────────────────────────

export interface ParsedItemClassification {
  type: 'laptop' | 'accessory' | 'unknown';
  raw_description: string;
  laptopSpecs?: {
    brand: string;
    model: string;
    raw_description: string;
    cpu?: string;
    ram?: string;
    storage?: string;
  };
  accessoryInfo?: {
    name: string;
    category: string;
  };
}

const KNOWN_LAPTOP_BRANDS = [
  { brand: 'HP', match: /\b(?:hp|hewlett[\s-]packard|elitebook|probook|zbook|pavilion|envy|spectre|victus|omen)\b/i },
  { brand: 'Dell', match: /\b(?:dell|latitude|precision|inspiron|vostro|xps|alienware|g-series)\b/i },
  { brand: 'Lenovo', match: /\b(?:lenovo|thinkpad|ideapad|legion|yoga|thinkbook)\b/i },
  { brand: 'Apple', match: /\b(?:apple|macbook|macbook[\s-]?air|macbook[\s-]?pro)\b/i },
  { brand: 'Asus', match: /\b(?:asus|zenbook|vivobook|rog|tuf|expertbook)\b/i },
  { brand: 'Acer', match: /\b(?:acer|aspire|swift|predator|nitro|travelmate)\b/i },
  { brand: 'MSI', match: /\b(?:msi|stealth|raider|modern|prestige|katana)\b/i },
  { brand: 'Toshiba', match: /\b(?:toshiba|dynabook|portege|satellite)\b/i },
  { brand: 'Microsoft', match: /\b(?:microsoft|surface|surface[\s-]?laptop)\b/i },
  { brand: 'Samsung', match: /\b(?:samsung|galaxy[\s-]?book)\b/i },
  { brand: 'Fujitsu', match: /\b(?:fujitsu|lifebook)\b/i },
];

const ACCESSORY_KEYWORDS = /\b(?:mouse|keyboard|charger|adapter|cable|bag|sleeve|case|battery|dock|docking|hub|stand|headset|earphones|headphone|caddy|thermal[\s-]?paste|screen[\s-]?panel|webcam|ram[\s-]?stick|nvme[\s-]?stick|hard[\s-]?drive)\b/i;

/**
 * Parses an invoice line item description into structured laptop or accessory data conservatively.
 * Cleanly isolates brand, model, cpu, ram, and storage without spec bleed into model.
 */
export function classifyInvoiceItem(description: string): ParsedItemClassification {
  const desc = description.trim();
  if (!desc) return { type: 'unknown', raw_description: description };

  // Check if it's explicitly an accessory first (unless it's clearly a laptop model name)
  if (ACCESSORY_KEYWORDS.test(desc) && !/\b(?:laptop|notebook|elitebook|latitude|thinkpad|macbook)\b/i.test(desc)) {
    let category = 'Other Accessories';
    if (/\b(?:charger|adapter|power[\s-]?supply)\b/i.test(desc)) category = 'Charger & Power';
    else if (/\b(?:ram|memory|ddr\d?)\b/i.test(desc)) category = 'RAM & Memory';
    else if (/\b(?:ssd|hdd|storage|nvme|hard[\s-]?drive)\b/i.test(desc)) category = 'SSD & Storage';
    else if (/\b(?:bag|sleeve|case|backpack)\b/i.test(desc)) category = 'Bag & Sleeve';
    else if (/\b(?:mouse|keyboard)\b/i.test(desc)) category = 'Mouse & Keyboard';
    else if (/\b(?:cable|hdmi|type-c|usb|vga|converter)\b/i.test(desc)) category = 'Cable & Adapter';
    else if (/\b(?:screen|panel|fan|hinge|spare)\b/i.test(desc)) category = 'Screen & Spare Parts';

    return {
      type: 'accessory',
      raw_description: desc,
      accessoryInfo: {
        name: desc,
        category,
      },
    };
  }

  // Look for laptop brand
  let detectedBrand: string | null = null;
  for (const b of KNOWN_LAPTOP_BRANDS) {
    if (b.match.test(desc)) {
      detectedBrand = b.brand;
      break;
    }
  }

  // ── 1. Extract CPU ──────────────────────────────────────────────────────────
  let detectedCpu: string | undefined;
  const fullCpuMatch = desc.match(
    /\b(?:core\s+)?(i[3579]|ryzen\s*[3579]|m[123](?:\s*pro|\s*max|\s*ultra)?|celeron|pentium|xeon)(?:[\s\/\-]+(\d{4,5}[A-Z]{0,2})|[\s\/\-]+(\d{1,2})(?:th|st|nd|rd)?(?:\s*(?:gen|generation))?)?\b/i
  );
  if (fullCpuMatch) {
    const chip = fullCpuMatch[1].toUpperCase().replace(/\s+/, ' ');
    const modelNum = fullCpuMatch[2];
    const gen = fullCpuMatch[3];
    if (modelNum) {
      detectedCpu = `Core ${chip}-${modelNum}`;
    } else if (gen) {
      detectedCpu = `Core ${chip} ${gen}th Gen`;
    } else {
      detectedCpu = `Core ${chip}`;
    }
  }

  // ── 2. Extract RAM ──────────────────────────────────────────────────────────
  let detectedRam: string | undefined;
  const ramExplicitMatch = desc.match(/\b(4|8|16|32|64|128)\s*(?:gb|g|gig|ddr[345]?)\b/i);
  if (ramExplicitMatch) {
    detectedRam = `${ramExplicitMatch[1]}GB`;
  } else {
    // Look for slash or space sequence: e.g. /16/256 or "11th 16 512"
    const ramSeqMatch = desc.match(/(?:[\s\/\-,])\b(4|8|16|32|64|128)\b(?=\s*[\/\-, ]\s*(?:128|256|512|1000|1024|1tb|2tb|\b))/i);
    if (ramSeqMatch) {
      detectedRam = `${ramSeqMatch[1]}GB`;
    }
  }

  // ── 3. Extract Storage ──────────────────────────────────────────────────────
  let detectedStorage: string | undefined;
  const storageExplicitMatch = desc.match(/\b(128|256|512|1000|1024)\s*(?:gb|g|ssd|nvme|hdd)?\b/i);
  const tbMatch = desc.match(/\b(1|2)\s*(?:tb|t)\s*(?:ssd|nvme|hdd)?\b/i);

  if (tbMatch) {
    detectedStorage = `${tbMatch[1]}TB SSD`;
  } else if (storageExplicitMatch) {
    const storageVal = storageExplicitMatch[1];
    if (storageVal !== ramExplicitMatch?.[1] || /\b(?:ssd|nvme|hdd|storage)\b/i.test(desc)) {
      detectedStorage = `${storageVal}GB SSD`;
    }
  }

  // ── 4. Extract Clean Model Name (Purity Guaranteed) ─────────────────────────
  if (detectedBrand || (detectedCpu && (detectedRam || detectedStorage))) {
    const brand = detectedBrand || 'Laptop';
    const rawTokens = desc
      .split(/[\/,;\t]+/)
      .map((t) => t.trim())
      .filter(Boolean);

    const modelTokens: string[] = [];

    for (let cIdx = 0; cIdx < rawTokens.length; cIdx++) {
      const chunk = rawTokens[cIdx];
      const words = chunk.split(/\s+/).filter(Boolean);
      const filteredWords: string[] = [];

      for (let wIdx = 0; wIdx < words.length; wIdx++) {
        const word = words[wIdx];
        const lower = word.toLowerCase();
        const prevWord = wIdx > 0 ? words[wIdx - 1].toLowerCase() : '';

        // Brand
        if (detectedBrand && (lower === detectedBrand.toLowerCase() || lower === 'hewlett-packard')) {
          continue;
        }

        // Full CPU chip e.g. i5-1145G7 or Core
        if (/^(?:core|i[3579]-?\d{4,5}[a-z]{0,2}|i[3579]|ryzen|celeron|pentium|xeon|m[123])$/i.test(lower)) {
          continue;
        }

        // CPU generation (e.g. 8th, 10th, 11th, 6th, 9th)
        // Preserve "Gen 9" when part of ThinkPad/Yoga/Carbon model name
        if (/^\d{1,2}(?:th|st|nd|rd)(?:[\s-]*(?:gen|generation))?$/i.test(lower)) {
          continue;
        }
        if (/^(?:gen|generation)$/i.test(lower) && !/^(?:carbon|yoga|x1|thinkpad)$/i.test(prevWord)) {
          continue;
        }

        // RAM token
        if (
          /^(?:4|8|16|32|64|128)(?:gb|g|gig)$/i.test(lower) ||
          /^(?:ddr[345]|ram)$/i.test(lower) ||
          (detectedRam && lower === detectedRam.replace('GB', '').toLowerCase())
        ) {
          continue;
        }

        // Storage token
        if (
          /^(?:128|256|512|1000|1024)(?:gb|g|ssd|nvme)?$/i.test(lower) ||
          /^(?:1|2)(?:tb|t)$/i.test(lower) ||
          /^(?:ssd|nvme|hdd|m\.?2|emmc|sata)$/i.test(lower) ||
          (detectedStorage && lower === detectedStorage.replace(/[^0-9]/g, ''))
        ) {
          continue;
        }

        // Display/cosmetic specs
        if (/^(?:fhd|uhd|4k|ips|touch|non-touch|screen|oled)$/i.test(lower)) {
          continue;
        }

        filteredWords.push(word);
      }

      if (filteredWords.length > 0) {
        modelTokens.push(filteredWords.join(' '));
      }
    }

    let modelCandidate = modelTokens.join(' ').replace(/[\\/|,;()]/g, ' ').replace(/\s+/g, ' ').trim();
    modelCandidate = modelCandidate
      .replace(/\b(?:128|256|512|1000|1024|1tb|2tb)\b/gi, '')
      .replace(/\b(?:4|8|16|32|64|128)\s*(?:gb|g)?\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    const model = modelCandidate || 'Standard Model';

    return {
      type: 'laptop',
      raw_description: desc,
      laptopSpecs: {
        brand,
        model,
        raw_description: desc,
        cpu: detectedCpu,
        ram: detectedRam,
        storage: detectedStorage,
      },
    };
  }

  return { type: 'unknown', raw_description: desc };
}

/**
 * Compares parsed brand and model against existing laptop inventory in database.
 * Used ONLY to canonicalize spelling and case (e.g. "hp" / "840 g6" -> "HP" / "840 G6" or "EliteBook 840 G6").
 * Never copies specs from old inventory and never fuzzy-matches different models.
 */
export function canonicalizeLaptopModel(
  parsedBrand: string,
  parsedModel: string,
  existingLaptops: Array<{ brand: string; model: string }>
): { brand: string; model: string; isCanonicalized: boolean } {
  const normBrand = parsedBrand.toLowerCase().trim();
  const normModel = parsedModel.toLowerCase().trim();

  if (!normModel || !existingLaptops || existingLaptops.length === 0) {
    return { brand: parsedBrand, model: parsedModel, isCanonicalized: false };
  }

  // 1. Exact match on brand + model (case-insensitive)
  const exact = existingLaptops.find(
    (l) =>
      l.brand.toLowerCase().trim() === normBrand &&
      l.model.toLowerCase().trim() === normModel
  );
  if (exact) {
    return { brand: exact.brand, model: exact.model, isCanonicalized: true };
  }

  // 2. Canonical prefix match for same brand:
  // e.g. parsed = "840 G6" matches existing = "EliteBook 840 G6"
  // e.g. parsed = "7420" matches existing = "Latitude 7420"
  const prefixMatches = existingLaptops.filter((l) => {
    if (l.brand.toLowerCase().trim() !== normBrand) return false;
    const em = l.model.toLowerCase().trim();
    const escaped = normModel.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    const regex = new RegExp(`(?:^|\\s)${escaped}$`, 'i');
    return regex.test(em);
  });

  if (prefixMatches.length === 1) {
    return {
      brand: prefixMatches[0].brand,
      model: prefixMatches[0].model,
      isCanonicalized: true,
    };
  }

  return { brand: parsedBrand, model: parsedModel, isCanonicalized: false };
}
